import { useToast } from '@chakra-ui/react'
import {
  faChevronLeft,
  faChevronRight,
  faEllipsis,
  faLink,
  faMagnifyingGlass,
  faPaperPlane,
  faUserMinus,
  faXmark,
} from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import type React from 'react'
import { useEffect, useMemo, useState } from 'react'
import DropdownCell from '../../../../components/DropdownCell'
import DropdownMenu from '../../../../components/DropdownMenu'
import InfoBox from '../../../../components/InfoBox'
import PageWrapper from '../../../../components/Pagewrapper'
import Toast from '../../../../components/Toast'
import AsyncAlert from '../../../../containers/AsyncAlert'
import {
  type MigrationStatus,
  type WealthManagerClientDto,
  useGetWealthManagerBookQuery,
  useRemindWealthManagerClientMutation,
  useUnlinkWealthManagerClientMutation,
} from '../../../../redux/services/coreApi'
import useAsyncAlert from '../../../../utils/useAsyncAlert'
import {
  STATUS_ORDER,
  STATUS_PRESENTATION,
  type StatusFilter,
  countByStatus,
  displayName,
  hasName,
  initials,
  matchesSearch,
} from './clientBook'

const PAGE_SIZE = 25

const MigrationStatusBadge: React.FC<{ status: MigrationStatus }> = ({
  status,
}) => {
  const { label, colorScheme } = STATUS_PRESENTATION[status]
  return (
    <div className="w-min">
      <InfoBox colorScheme={colorScheme}>{label}</InfoBox>
    </div>
  )
}

// Where the book stands overall: how many clients have finished onboarding out of the whole book.
const ProgressSummary: React.FC<{ total: number; onboarded: number }> = ({
  total,
  onboarded,
}) => {
  const percent = total === 0 ? 0 : Math.round((onboarded / total) * 100)
  return (
    <div className="card gap-3">
      <div className="flex items-end justify-between gap-4">
        <div>
          <p className="text-description">Onboarded</p>
          <p className="text-default-large">
            {onboarded}{' '}
            <span className="text-description-large">of {total} clients</span>
          </p>
        </div>
        <p className="text-default-large">{percent}%</p>
      </div>
      <div
        className="h-2 w-full rounded-full bg-black/5 dark:bg-white/10 overflow-hidden"
        role="progressbar"
        aria-label="Clients onboarded"
        aria-valuemin={0}
        aria-valuemax={total}
        aria-valuenow={onboarded}
      >
        <div
          className="h-full rounded-full bg-green-600 transition-all"
          style={{ width: `${percent}%` }}
        />
      </div>
    </div>
  )
}

const SearchInput: React.FC<{
  value: string
  onChange: (value: string) => void
}> = ({ value, onChange }) => (
  <div className="input-card flex items-center !pl-0 border border-solid border-brand-light-card-border dark:border-brand-dark-card-border">
    <FontAwesomeIcon
      icon={faMagnifyingGlass}
      className="w-3.5 h-3.5 mx-3 shrink-0 color-text-description"
    />
    <input
      type="search"
      aria-label="Search clients"
      placeholder="Search by name or email"
      className="h-full w-full bg-transparent border-0 outline-none focus:ring-0 p-0 text-sm font-medium color-text-default placeholder:text-brand-light-text-subtle-description [&::-webkit-search-cancel-button]:hidden"
      value={value}
      onChange={e => onChange(e.target.value)}
    />
    {value && (
      <button
        type="button"
        aria-label="Clear search"
        onClick={() => onChange('')}
        className="w-8 h-8 mr-1.5 shrink-0 rounded-md flex items-center justify-center color-text-description hover:bg-black/5 dark:hover:bg-white/10"
      >
        <FontAwesomeIcon icon={faXmark} className="w-3 h-3" />
      </button>
    )}
  </div>
)

// One chip per rung of the ladder with its count, plus "All". A rung nobody is on is left out to keep the
// row short — unless it is the active filter, so the selection never disappears from under the WM.
const StatusFilterChips: React.FC<{
  counts: Record<MigrationStatus, number>
  total: number
  value: StatusFilter
  onChange: (value: StatusFilter) => void
}> = ({ counts, total, value, onChange }) => {
  const chips: { key: StatusFilter; label: string; count: number }[] = [
    { key: 'All', label: 'All', count: total },
    ...STATUS_ORDER.filter(s => counts[s] > 0 || s === value).map(s => ({
      key: s,
      label: STATUS_PRESENTATION[s].label,
      count: counts[s],
    })),
  ]
  return (
    <div
      className="flex flex-wrap gap-2"
      role="group"
      aria-label="Filter by status"
    >
      {chips.map(chip => {
        const active = chip.key === value
        return (
          <button
            key={chip.key}
            type="button"
            aria-pressed={active}
            onClick={() => onChange(chip.key)}
            className={`h-8 px-3 rounded-full text-xs font-semi flex items-center gap-1.5 border border-solid transition-colors ${
              active
                ? 'bg-brand-dark-background text-white border-brand-dark-background dark:bg-white dark:text-brand-light-text-default dark:border-white'
                : 'bg-brand-light-card dark:bg-brand-dark-card color-text-default border-brand-light-card-border dark:border-brand-dark-card-border hover:bg-black/5 dark:hover:bg-white/10'
            }`}
          >
            {chip.label}
            <span className={active ? 'opacity-70' : 'color-text-description'}>
              {chip.count}
            </span>
          </button>
        )
      })}
    </div>
  )
}

const MenuIcon: React.FC<{ icon: typeof faLink; danger?: boolean }> = ({
  icon,
  danger,
}) => (
  <div className="w-8 h-8 shrink-0 flex items-center justify-center">
    <FontAwesomeIcon
      icon={icon}
      className={`w-3.5 h-3.5 ${danger ? 'text-brand-light-red' : 'color-text-description'}`}
    />
  </div>
)

// Per-client actions, behind one menu so a long book stays readable. Copy-link and remind exist only for a
// not-yet-activated client (ADR-0006 §4/§5); removing a client from the book is offered on every row,
// whatever the status, so its presence says nothing about a client's compliance state (ADR-0016).
const ClientActions: React.FC<{
  client: WealthManagerClientDto
  reminded: boolean
  onCopyLink: (client: WealthManagerClientDto) => void
  onRemind: (client: WealthManagerClientDto) => void
  onRemove: (client: WealthManagerClientDto) => void
}> = ({ client, reminded, onCopyLink, onRemind, onRemove }) => {
  const notActivated = client.status === 'NotActivated'
  const items = [
    ...(notActivated && client.activationLink
      ? [
          <DropdownCell
            key="copy"
            title="Copy activation link"
            subtitle="Send it to the client through your own channels"
            icon={<MenuIcon icon={faLink} />}
            onClick={() => onCopyLink(client)}
          />,
        ]
      : []),
    ...(notActivated
      ? [
          <DropdownCell
            key="remind"
            title={reminded ? 'Reminder sent' : 'Send reminder email'}
            subtitle="Emails the activation link to the client"
            icon={<MenuIcon icon={faPaperPlane} />}
            disabled={reminded}
            onClick={() => onRemind(client)}
          />,
        ]
      : []),
    <DropdownCell
      key="remove"
      title="Remove from my book"
      subtitle="Ends the link between you and this client"
      icon={<MenuIcon icon={faUserMinus} danger />}
      onClick={() => onRemove(client)}
    />,
  ]

  return (
    <DropdownMenu placement="bottom-end" portalWrap renderList={items}>
      <span
        aria-label={`Actions for ${displayName(client)}`}
        className="w-9 h-9 rounded-lg flex items-center justify-center color-text-description hover:bg-black/5 dark:hover:bg-white/10 transition-colors"
      >
        <FontAwesomeIcon icon={faEllipsis} />
      </span>
    </DropdownMenu>
  )
}

// On a phone the status drops under the name (aligned with the text, past the avatar) so the name gets the
// full row width; from `sm` up it takes its own column. One badge either way, placed by grid position.
const ClientRow: React.FC<{
  client: WealthManagerClientDto
  actions: React.ReactNode
}> = ({ client, actions }) => (
  <li className="grid grid-cols-[minmax(0,1fr)_auto] sm:grid-cols-[minmax(0,1fr)_auto_auto] items-center gap-x-3 gap-y-1.5 sm:gap-x-6 px-4 sm:px-6 py-3 border-t border-solid border-brand-light-card-border dark:border-brand-dark-card-border first:border-t-0">
    <div className="flex items-center gap-3 min-w-0">
      <div
        aria-hidden
        className="w-9 h-9 shrink-0 rounded-full bg-black/5 dark:bg-white/10 flex items-center justify-center text-xs font-semi color-text-description"
      >
        {initials(client)}
      </div>
      <div className="min-w-0">
        <p className="text-sm font-semi color-text-default truncate">
          {displayName(client)}
        </p>
        <p className="text-description truncate">
          {hasName(client) ? client.email : 'No name on file'}
        </p>
      </div>
    </div>
    <div className="col-start-1 row-start-2 pl-12 sm:pl-0 sm:col-start-2 sm:row-start-1">
      <MigrationStatusBadge status={client.status} />
    </div>
    <div className="col-start-2 row-start-1 row-span-2 sm:col-start-3 sm:row-span-1">
      {actions}
    </div>
  </li>
)

const Pagination: React.FC<{
  page: number
  pageCount: number
  total: number
  onChange: (page: number) => void
}> = ({ page, pageCount, total, onChange }) => {
  const from = page * PAGE_SIZE + 1
  const to = Math.min(total, (page + 1) * PAGE_SIZE)
  const navButton =
    'w-8 h-8 rounded-lg flex items-center justify-center color-text-default hover:bg-black/5 dark:hover:bg-white/10 disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-transparent'
  return (
    <div className="flex items-center justify-between gap-4 px-4 sm:px-6 py-3 border-t border-solid border-brand-light-card-border dark:border-brand-dark-card-border">
      <p className="text-description">
        {from}–{to} of {total}
      </p>
      <div className="flex items-center gap-1">
        <button
          type="button"
          aria-label="Previous page"
          className={navButton}
          disabled={page === 0}
          onClick={() => onChange(page - 1)}
        >
          <FontAwesomeIcon icon={faChevronLeft} className="w-3 h-3" />
        </button>
        <button
          type="button"
          aria-label="Next page"
          className={navButton}
          disabled={page >= pageCount - 1}
          onClick={() => onChange(page + 1)}
        >
          <FontAwesomeIcon icon={faChevronRight} className="w-3 h-3" />
        </button>
      </div>
    </div>
  )
}

const EmptyState: React.FC<{ title: string; children?: React.ReactNode }> = ({
  title,
  children,
}) => (
  <div className="py-16 px-6 flex flex-col items-center text-center gap-2">
    <p className="text-default-base">{title}</p>
    {children}
  </div>
)

// The wealth-manager area: a signed-in active WM finds, filters and acts on the Veli clients in their own
// book as they move through migration. Identity + status only — no balances (ADR-0006 §6). Scoping and
// the active-WM gate are enforced server-side; this screen renders what the endpoint returns for the caller.
const WealthManagerScreen: React.FC = () => {
  const toast = useToast()
  const { data: bookData, isLoading, isError } = useGetWealthManagerBookQuery()
  const clients = bookData?.clients ?? []

  const [query, setQuery] = useState('')
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('All')
  const [page, setPage] = useState(0)
  // Reminders have no server-side throttle (ADR-0006 §5); the only guard against an accidental double
  // send is disabling the item for the rest of this view. UI-only, not a guarantee.
  const [reminded, setReminded] = useState<Set<string>>(new Set())

  const [remindClient] = useRemindWealthManagerClientMutation()
  // Removing a client from the book (ADR-0016) is confirmed first: the WM cannot undo it themselves.
  const [unlinkClient, { isError: unlinkError, reset: resetUnlink }] =
    useUnlinkWealthManagerClientMutation()
  const { openAlertAndAwaitConfirmation, alertProps } = useAsyncAlert<
    WealthManagerClientDto,
    WealthManagerClientDto
  >()

  const counts = useMemo(() => countByStatus(clients), [clients])
  const filtered = useMemo(
    () =>
      clients.filter(
        client =>
          (statusFilter === 'All' || client.status === statusFilter) &&
          matchesSearch(client, query)
      ),
    [clients, statusFilter, query]
  )
  const pageCount = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE))
  const visible = filtered.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE)

  // Back to the first page when the filter changes; stay in range when a removal shrinks the list.
  const onQueryChange = (value: string) => {
    setQuery(value)
    setPage(0)
  }
  const onStatusFilterChange = (value: StatusFilter) => {
    setStatusFilter(value)
    setPage(0)
  }
  useEffect(() => {
    if (page > pageCount - 1) setPage(pageCount - 1)
  }, [page, pageCount])

  const notify = (type: 'success' | 'error', message: string) =>
    toast({
      position: 'top-right',
      render: () => <Toast type={type}>{message}</Toast>,
    })

  const onCopyLink = async (client: WealthManagerClientDto) => {
    if (!client.activationLink) return
    try {
      await navigator.clipboard.writeText(client.activationLink)
      notify('success', 'Activation link copied')
    } catch {
      notify('error', 'Couldn’t copy the link. Please try again.')
    }
  }

  const onRemind = async (client: WealthManagerClientDto) => {
    try {
      await remindClient(client.veliUserId).unwrap()
      setReminded(prev => new Set(prev).add(client.veliUserId))
      notify('success', `Reminder sent to ${client.email}`)
    } catch {
      notify('error', 'Couldn’t send the reminder. Please try again.')
    }
  }

  const onRemove = (client: WealthManagerClientDto) => {
    resetUnlink()
    openAlertAndAwaitConfirmation(client, client)
  }

  const clearFilters = () => {
    onQueryChange('')
    onStatusFilterChange('All')
  }

  const renderList = () => {
    if (isError)
      return (
        <EmptyState title="We couldn’t load your clients">
          <p className="text-description">Please reload the page.</p>
        </EmptyState>
      )
    if (clients.length === 0)
      return (
        <EmptyState title="No clients yet">
          <p className="text-description max-w-sm">
            Clients appear here once they’re linked to your advisor account.
          </p>
        </EmptyState>
      )
    if (filtered.length === 0)
      return (
        <EmptyState title="No clients match your search">
          <button
            type="button"
            onClick={clearFilters}
            className="text-sm font-semi color-text-link underline"
          >
            Clear search and filters
          </button>
        </EmptyState>
      )
    return (
      <>
        <div className="hidden sm:grid grid-cols-[minmax(0,1fr)_auto_auto] gap-6 px-6 pt-4 pb-3 border-b border-solid border-brand-light-card-border dark:border-brand-dark-card-border">
          <div className="table-head">Client</div>
          <div className="table-head">Status</div>
          <div className="w-9" />
        </div>
        <ul aria-label="Clients">
          {visible.map(client => (
            <ClientRow
              key={client.veliUserId}
              client={client}
              actions={
                <ClientActions
                  client={client}
                  reminded={reminded.has(client.veliUserId)}
                  onCopyLink={onCopyLink}
                  onRemind={onRemind}
                  onRemove={onRemove}
                />
              }
            />
          ))}
        </ul>
        {filtered.length > PAGE_SIZE && (
          <Pagination
            page={page}
            pageCount={pageCount}
            total={filtered.length}
            onChange={setPage}
          />
        )}
      </>
    )
  }

  return (
    <PageWrapper
      title="My client book"
      description="Your Veli clients and where each one is in their move to Penning."
      isLoading={isLoading}
    >
      <div className="p-4 lg:p-8 flex flex-col gap-4 max-w-5xl w-full">
        {clients.length > 0 && (
          <ProgressSummary
            total={clients.length}
            onboarded={counts.Onboarded}
          />
        )}

        {clients.length > 0 && (
          <div className="flex flex-col gap-3">
            <SearchInput value={query} onChange={onQueryChange} />
            <StatusFilterChips
              counts={counts}
              total={clients.length}
              value={statusFilter}
              onChange={onStatusFilterChange}
            />
          </div>
        )}

        {!isLoading && (
          <div className="bg-brand-light-card dark:bg-brand-dark-card rounded-lg border border-solid border-brand-light-card-border dark:border-brand-dark-card-border overflow-hidden">
            {renderList()}
          </div>
        )}
      </div>

      <AsyncAlert
        title={client =>
          unlinkError
            ? 'Something went wrong'
            : `Remove ${client ? displayName(client) : 'this client'} from your book?`
        }
        bodyText={client =>
          unlinkError
            ? 'We couldn’t remove this client. Please reload the page and try again.'
            : `${client ? displayName(client) : 'The client'} will no longer be linked to your advisor account and will disappear from your book. Their relationship with Penning is not affected — they continue as a direct client. You can’t undo this yourself; contact Penning support if the client needs to be re-linked.`
        }
        submitButtonText={unlinkError ? 'Close' : 'Remove client'}
        onClick={async client => {
          if (unlinkError || !client) return
          await unlinkClient(client.veliUserId).unwrap()
          notify('success', `${displayName(client)} was removed from your book`)
        }}
        {...alertProps}
      />
    </PageWrapper>
  )
}

export default WealthManagerScreen
